// „Neuer Eigentümer“ als Drehkarte (Handoff Eigentümer §6.4): Entwurf, Prüfung, Hinweise,
// Belegung der Einheiten, Rollen je Objekt und der Schreibplan. Reine Funktionen – testbar.
//
// Datenmodell: ganzes Objekt der Mietverwaltung → objekt_eigentuemer; sonst je Einheit eine
// Zeile in einheit_eigentuemer (seit 13.10.2026 mit Historie: Vorbesitzer enden am Vortag,
// Miteigentümer bekommen eine eigene Zeile) plus objekt_eigentuemer als Objektverknüpfung
// (liest die KI). Rolle: Objekt-Verwaltungsart, in eigener WEG zusätzlich sev_status 'SEV'.

import { emailGueltig } from '../immobilien/portal';
import { vortag } from '../mieter/mietstaende';
import { eigentuemerAnzeigename } from './anzeige';
import type { EigentuemerRoh, EinheitEigRoh, EinheitRoh, ObjektEigRoh, ObjektRoh } from './liste';
import type { RolleKurz } from './rollen';
import { istAktuell } from './einheitEigentum';

export type EigAnrede = 'Frau' | 'Herr' | 'Eheleute' | 'Firma';
export const EIG_ANREDEN: readonly EigAnrede[] = ['Frau', 'Herr', 'Eheleute', 'Firma'];

/** Belegte Einheiten: Eigentümerwechsel (Vorbesitzer gibt ab) oder Miteigentümer (kommt hinzu). */
export type WechselArt = 'wechsel' | 'miteigentuemer';

export interface NeuerEigentuemerEntwurf {
  anrede: EigAnrede;
  firma: string;
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  stadt: string;
  objektId: string | null;
  einheitIds: string[];
  art: WechselArt;
  beginn: string;
  ende: string;
  iban: string;
  kontoinhaber: string;
  steuerId: string;
  steuerNr: string;
  notizen: string;
  /** Verwaltervergütung je Einheit und Monat in € (nur MV/SEV); 0 = nicht vereinbart */
  verguetung: number;
  /** in eigener WEG: zusätzlich Sondereigentumsverwaltung */
  sev: boolean;
}

export const LEERER_EIG_ENTWURF: NeuerEigentuemerEntwurf = {
  anrede: 'Frau', firma: '', vorname: '', nachname: '', email: '', telefon: '', strasse: '', hausnummer: '', plz: '', stadt: '',
  objektId: null, einheitIds: [], art: 'wechsel', beginn: '', ende: '', iban: '', kontoinhaber: '', steuerId: '', steuerNr: '', notizen: '', verguetung: 0, sev: false,
};

/** Art der Verwaltung des Objekts: MV (auch GV/leer), eigene WEG, fremd verwaltete WEG (SEV). */
export type ObjektArt = 'MV' | 'WEG' | 'SEV';
export function objektArt(verwaltungsart: string | null | undefined): ObjektArt {
  const v = (verwaltungsart ?? '').trim().toUpperCase();
  return v === 'WEG' ? 'WEG' : v === 'SEV' ? 'SEV' : 'MV';
}

export const ROLLEN_TEXT: Record<RolleKurz, string> = { MV: 'Mietverwaltung', WEG: 'WEG-Mitglied', SEV: 'Sondereigentumsverwaltung' };

/** Wählbare Rollen je Objekt: MV-Objekt → MV; eigene WEG → WEG (fest) + SEV optional; fremde WEG → SEV. */
export function rollenOptionen(art: ObjektArt): { rolle: RolleKurz; fest: boolean }[] {
  if (art === 'WEG') return [{ rolle: 'WEG', fest: true }, { rolle: 'SEV', fest: false }];
  return [{ rolle: art, fest: true }];
}

export function gewaehlteRollen(art: ObjektArt, sev: boolean): RolleKurz[] {
  return art === 'WEG' ? (sev ? ['WEG', 'SEV'] : ['WEG']) : [art];
}

/** Verwaltervergütung gibt es nur bei MV/SEV – bei reiner WEG läuft sie über das Hausgeld. */
export const mitVerguetung = (rollen: readonly RolleKurz[]): boolean => rollen.includes('MV') || rollen.includes('SEV');

export interface Besitzer { id: string; name: string }

export interface EinheitEigentum {
  id: string;
  bezeichnung: string;
  /** laufende Zuordnungen in einheit_eigentuemer (Eigentümer und Miteigentümer) */
  einheitBesitzer: Besitzer[];
  /** Anzeige „Eigentum · Name“: Einheitseigentümer, bei MV sonst die Objekteigentümer */
  anzeige: Besitzer[];
}

const laeuft = (bis: string | null, heute: string): boolean => !bis || bis.slice(0, 10) >= heute;
const sortierer = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

export interface EigentumsDaten {
  eigentuemer: readonly EigentuemerRoh[];
  objektEig: readonly ObjektEigRoh[];
  einheitEig: readonly EinheitEigRoh[];
  einheiten: readonly EinheitRoh[];
}

/** Offene Objekteigentümer (vertrag_bis leer oder in der Zukunft). */
export function objektBesitzer(objektId: string, d: EigentumsDaten, heute: string): Besitzer[] {
  const namen = new Map(d.eigentuemer.map(e => [e.id, eigentuemerAnzeigename(e)]));
  return d.objektEig
    .filter(r => r.objekt_id === objektId && laeuft(r.vertrag_bis, heute))
    .map(r => ({ id: r.eigentuemer_id, name: namen.get(r.eigentuemer_id) ?? 'Unbekannt' }));
}

/** Einheiten eines Objekts mit ihrem aktuellen Eigentum (sortiert nach Bezeichnung). */
export function einheitenMitEigentum(objektId: string, art: ObjektArt, d: EigentumsDaten, heute: string): EinheitEigentum[] {
  const namen = new Map(d.eigentuemer.map(e => [e.id, eigentuemerAnzeigename(e)]));
  const jeEinheit = new Map<string, Besitzer[]>();
  for (const r of d.einheitEig) {
    if (!istAktuell(r, heute)) continue;
    jeEinheit.set(r.einheit_id, [...(jeEinheit.get(r.einheit_id) ?? []), { id: r.eigentuemer_id, name: namen.get(r.eigentuemer_id) ?? 'Unbekannt' }]);
  }
  const objekt = art === 'MV' ? objektBesitzer(objektId, d, heute) : [];
  return d.einheiten
    .filter(e => e.objekt_id === objektId)
    .map(e => {
      const eb = jeEinheit.get(e.id) ?? [];
      return { id: e.id, bezeichnung: (e.bezeichnung ?? '').trim() || 'Einheit', einheitBesitzer: eb, anzeige: eb.length ? eb : objekt };
    })
    .sort((a, b) => sortierer.compare(a.bezeichnung, b.bezeichnung));
}

/**
 * Wohin wird geschrieben? Ganzes Objekt der Mietverwaltung (alle Einheiten bzw. keine angelegt)
 * → nur objekt_eigentuemer; sonst je Einheit.
 */
export function zielEbene(art: ObjektArt, einheiten: readonly EinheitEigentum[], gewaehlt: readonly string[]): 'objekt' | 'einheiten' {
  if (einheiten.length === 0) return 'objekt';
  const alle = einheiten.every(e => gewaehlt.includes(e.id));
  return art === 'MV' && alle ? 'objekt' : 'einheiten';
}

export interface Belegung {
  ebene: 'objekt' | 'einheiten';
  /** Vorbesitzer, die beim Wechsel abgeben (je Ebene) */
  vorbesitzer: Besitzer[];
  /** belegte gewählte Einheiten (nur Ebene „einheiten“) */
  belegteEinheiten: EinheitEigentum[];
}

export function belegung(art: ObjektArt, einheiten: readonly EinheitEigentum[], gewaehlt: readonly string[], objektEigentuemer: readonly Besitzer[]): Belegung {
  const ebene = zielEbene(art, einheiten, gewaehlt);
  if (ebene === 'objekt') return { ebene, vorbesitzer: [...objektEigentuemer], belegteEinheiten: [] };
  const belegte = einheiten.filter(e => gewaehlt.includes(e.id) && e.einheitBesitzer.length > 0);
  const vorbesitzer = new Map<string, Besitzer>();
  for (const e of belegte) for (const v of e.einheitBesitzer) vorbesitzer.set(v.id, v);
  return { ebene, vorbesitzer: [...vorbesitzer.values()], belegteEinheiten: belegte };
}

export const anzeigeName = (e: Pick<NeuerEigentuemerEntwurf, 'anrede' | 'firma' | 'vorname' | 'nachname'>): string =>
  eigentuemerAnzeigename({ anrede: e.anrede, firma_name: e.firma, vorname: e.vorname, nachname: e.nachname });

export function schritteErledigt(e: NeuerEigentuemerEntwurf, anzahlEinheitenImObjekt: number): [boolean, boolean, boolean] {
  const s1 = e.anrede === 'Firma' ? !!e.firma.trim() : !!(e.vorname.trim() && e.nachname.trim());
  const s2 = !!e.objektId && (anzahlEinheitenImObjekt === 0 || e.einheitIds.length > 0);
  return [s1, s2, !!e.beginn];
}

export type EigFeld = 'email' | 'ende' | 'iban' | 'plz' | 'steuerId' | 'verguetung';

export function feldFehler(e: NeuerEigentuemerEntwurf): Partial<Record<EigFeld, string>> {
  const f: Partial<Record<EigFeld, string>> = {};
  if (e.email.trim() && !emailGueltig(e.email)) f.email = 'Bitte eine gültige E-Mail-Adresse eingeben';
  if (e.ende && e.beginn && e.ende < e.beginn) f.ende = 'Vertragsende liegt vor dem Vertragsbeginn';
  const iban = e.iban.replace(/\s+/g, '');
  if (iban && !/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/i.test(iban)) f.iban = 'IBAN ist unvollständig';
  if (e.plz.trim() && !/^\d{4,5}$/.test(e.plz.trim())) f.plz = 'PLZ prüfen';
  const sid = e.steuerId.replace(/\s+/g, '');
  if (sid && !/^\d{11}$/.test(sid)) f.steuerId = 'Steuer-ID hat 11 Ziffern';
  if (!Number.isFinite(e.verguetung) || e.verguetung < 0) f.verguetung = 'Vergütung prüfen';
  return f;
}

/** Hinweis in der Fußzeile (Texte wie im Prototyp). */
export function hinweis(e: NeuerEigentuemerEntwurf, anzahlEinheitenImObjekt: number): { text: string; bereit: boolean } {
  const [s1, s2, s3] = schritteErledigt(e, anzahlEinheitenImObjekt);
  if (!s1) return { text: e.anrede === 'Firma' ? 'Noch offen: Firmenname' : 'Noch offen: Vor- und Nachname', bereit: false };
  if (!s2) return { text: 'Noch offen: Objekt und Einheiten', bereit: false };
  if (!s3) return { text: 'Noch offen: Vertragsbeginn', bereit: false };
  if (Object.keys(feldFehler(e)).length > 0) return { text: 'Bitte die markierten Felder prüfen', bereit: false };
  return { text: 'Alles bereit – Eigentümer kann angelegt werden', bereit: true };
}

/** „Lorenz gibt ab – wird zum Stichtag ehemaliger Eigentümer, Ordner wandert mit.“ */
export function wechselText(vorbesitzer: readonly Besitzer[], beginn: string): string {
  const namen = vorbesitzer.map(b => b.name).join(', ');
  const stichtag = beginn ? `endet am ${deTag(vortag(beginn))}` : 'wird zum Stichtag ehemaliger Eigentümer';
  return `${namen} ${vorbesitzer.length > 1 ? 'geben' : 'gibt'} ab – ${stichtag}, Ordner wandert mit.`;
}

const deTag = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

/** Datensatz für `eigentuemer` – Firma über die Anrede „Firma“ (lib/eigentuemer/anzeige.ts). */
export function eigentuemerPayload(e: NeuerEigentuemerEntwurf, rollen: readonly RolleKurz[]): Record<string, string | number | null> {
  const leer = (s: string): string | null => (s.trim() ? s.trim() : null);
  return {
    anrede: e.anrede,
    firma_name: e.anrede === 'Firma' ? e.firma.trim() : null,
    vorname: e.vorname.trim(),
    nachname: e.nachname.trim(),
    strasse: leer(e.strasse),
    hausnummer: leer(e.hausnummer),
    plz: leer(e.plz),
    stadt: leer(e.stadt),
    bankverbindung: leer(e.iban.replace(/\s+/g, ' ')),
    steuer_id: leer(e.steuerId.replace(/\s+/g, '')),
    steuer_nr: leer(e.steuerNr),
    kontoinhaber: leer(e.kontoinhaber),
    verguetung_je_einheit: mitVerguetung(rollen) && e.verguetung > 0 ? Math.round(e.verguetung * 100) / 100 : null,
    notizen: leer(e.notizen),
    status: 'Aktiv',
    vertragsstart: e.beginn || null,
    vertragsende: e.ende || null,
  };
}

export interface EinheitSchreiben {
  einheitId: string;
  /** Wechsel: diese Vorbesitzer enden am Vortag des Stichtags (Ordner wandert) */
  vorbesitzerIds: string[];
  /** Wechsel: MEA, Hausgeld und Rücklage aus der Zeile dieses Vorbesitzers übernehmen */
  werteVon: string | null;
}

export interface SchreibPlan {
  ebene: 'objekt' | 'einheiten';
  objektId: string;
  /** objekt_eigentuemer immer (Objektverknüpfung) */
  objektLink: { vertragAb: string; vertragBis: string | null };
  /** Ebene „einheiten“: je gewählter Einheit eine neue Zeile in einheit_eigentuemer */
  einheiten: EinheitSchreiben[];
  sevStatus: 'SEV' | null;
  /** Ebene Objekt: Ordnerwechsel je Vorbesitzer (vertrag_bis setzt die Ausführung) */
  objektWechselVon: string[];
  /** Vorbesitzer, deren Objektverknüpfung am Vortag endet, weil ihnen dort keine Einheit bleibt */
  objektLinkEndet: string[];
  /** Stichtag minus ein Tag – Ende der Vorbesitzer */
  vorbesitzerBis: string | null;
}

/** Was geschrieben wird – unabhängig von Supabase, damit testbar. */
export function schreibPlan(
  e: NeuerEigentuemerEntwurf, art: ObjektArt, einheiten: readonly EinheitEigentum[], b: Belegung, alleEinheitEig: readonly EinheitEigRoh[], heute: string,
): SchreibPlan | null {
  if (!e.objektId) return null;
  const wechsel = e.art === 'wechsel';
  const basis = {
    ebene: b.ebene, objektId: e.objektId, objektLink: { vertragAb: e.beginn, vertragBis: e.ende || null },
    sevStatus: art === 'WEG' && e.sev ? 'SEV' as const : null, vorbesitzerBis: e.beginn ? vortag(e.beginn) : null,
  };
  if (b.ebene === 'objekt') {
    return { ...basis, einheiten: [], objektWechselVon: wechsel ? b.vorbesitzer.map(v => v.id) : [], objektLinkEndet: [] };
  }
  const schreiben: EinheitSchreiben[] = einheiten.filter(x => e.einheitIds.includes(x.id)).map(x => (
    wechsel && x.einheitBesitzer.length > 0
      ? { einheitId: x.id, vorbesitzerIds: x.einheitBesitzer.map(v => v.id), werteVon: x.einheitBesitzer[0]?.id ?? null }
      : { einheitId: x.id, vorbesitzerIds: [], werteVon: null }
  ));
  // Vorbesitzer, denen nach dem Wechsel in diesem Objekt keine laufende Einheit bleibt
  const abgegeben = new Set(schreiben.flatMap(s => s.vorbesitzerIds.map(v => `${s.einheitId}|${v}`)));
  const objektEinheiten = new Set(einheiten.map(x => x.id));
  const alle = [...new Set(schreiben.flatMap(s => s.vorbesitzerIds))];
  const objektLinkEndet = alle.filter(alt => !alleEinheitEig.some(r =>
    r.eigentuemer_id === alt && objektEinheiten.has(r.einheit_id) && !abgegeben.has(`${r.einheit_id}|${alt}`) && laeuft(r.vertrag_bis, heute)));
  return { ...basis, einheiten: schreiben, objektWechselVon: [], objektLinkEndet };
}

/** Objekte aus den Rohdaten für die Auswahl (mit Verwaltungsart). */
export function objektArtAus(objekte: readonly ObjektRoh[], id: string | null): ObjektArt {
  return objektArt(objekte.find(o => o.id === id)?.verwaltungsart);
}
